import React, { useCallback } from "react";
import { get } from "lodash/fp";
import { useSelector, useDispatch } from "react-redux";
import {
	change,
	Field,
	formValueSelector,
	getFormSyncErrors,
} from "redux-form";

import { CustomDropdownByValue } from "components";
import Button from "components/button";
import { FormColumn, FormRow, FormTitle } from "components/layout";
import { processors, processorsTypes } from "mappers";
import { uiActions } from "store";
import { EnumDto } from "types";
import { processorHasRepeatedValue } from "validators/validation";

import { FdNorthFields } from "./fdNorthFields";
import { FdOmahaFields } from "./fdOmahaFields";
import { OtherFields } from "./otherFields";
import { TsysFields } from "./tsysFields";

import styles from "./processorSpecific.module.scss";

type ProcessorSpecificProps = {
	loading: boolean;
	formName: string;
	index: number;
	length: number;
	name: string;
	remove: Function;
	fdOmahaProcessorTypes: EnumDto[];
	processorSpecific: any;
	deleteLegacyInformation: any;
};

export const ProcessorSpecificSection = React.memo(
	({
		loading,
		formName,
		index,
		length,
		name,
		remove,
		fdOmahaProcessorTypes,
		processorSpecific,
		deleteLegacyInformation,
	}: ProcessorSpecificProps) => {
		const dispatch = useDispatch<any>();

		const selector = formValueSelector(formName);
		const processor = useSelector(state =>
			selector(state, `${name}.processor`),
		);
		const formErrors = useSelector(state =>
			get(`${name}.processor`, getFormSyncErrors(formName)(state)),
		);

		const ProcessorSpecificFields = useCallback(
			name => {
				if (formErrors && processor !== processorsTypes.FISERV_OMAHA)
					return <></>;
				if (processor === processorsTypes.TSYS)
					return <TsysFields loading={loading} name={name} />;
				else if (processor === processorsTypes.FISERV_OMAHA)
					return (
						<FdOmahaFields
							loading={loading}
							name={name}
							fdOmahaProcessorTypes={fdOmahaProcessorTypes}
						/>
					);
				else if (processor === processorsTypes.FISERV_NORTH)
					return <FdNorthFields loading={loading} name={name} />;
				else if (processor === processorsTypes.OTHER)
					return <OtherFields loading={loading} name={name} />;
				else return null;
			},
			[formErrors, processor, loading, fdOmahaProcessorTypes],
		);

		const handleRemove = () => {
			const id = processorSpecific[index]?.id;

			if (id && deleteLegacyInformation) {
				deleteLegacyInformation({ id })
					.then(() => {
						dispatch(
							uiActions.showSuccessNotification({
								title: "Processor has been successfully removed.",
							}),
						);
						remove(index);
					})
					.catch(error => {
						dispatch(
							uiActions.showErrorNotification({
								title: error,
							}),
						);
					});
			} else {
				remove(index);
			}
		};

		const handleChangeStatus = (event, newValue) => {
			dispatch(change(`${formName}`, `${name}`, {}));
			event.target.blur();
		};

		return (
			<>
				<FormRow>
					<FormColumn>
						{length > 0 && index === 0 && (
							<FormTitle>
								<h3>Processor Specific</h3>
							</FormTitle>
						)}
					</FormColumn>
					<FormColumn className="text-right">
						<Button
							value="Remove processor"
							variant="secondary"
							onClick={handleRemove}
							className={styles.btnRemove}
						/>
					</FormColumn>
				</FormRow>
				<FormRow>
					<FormColumn>
						<Field
							component={CustomDropdownByValue}
							name={`${name}.processor`}
							labelName="name"
							smLabel={5}
							validate={processorHasRepeatedValue}
							onChange={handleChangeStatus}
							smSelect={7}
							label="Processor"
							items={processors}
							loading={loading}
							hideRequiredMark
						/>
					</FormColumn>
				</FormRow>

				{ProcessorSpecificFields(name)}
			</>
		);
	},
);
